import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AuthLayout } from "@/components/auth/auth-layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { Loader2, MessageCircle, RefreshCw } from "lucide-react";

export const Route = createFileRoute("/activate")({ component: ActivatePage });

// Account linking is done entirely in the ClipsFlow bot via the /link command,
// which opens a one-tap Google sign-in and links your Telegram to your FriskyDev
// account (frisky_account_id). There is NO code to paste — once you finish the
// sign-in this page detects the link and forwards you to the dashboard.
function ActivatePage() {
  const { session, loading, roleLoading, telegramId, refreshRole, signOut } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!session) { navigate({ to: "/login" }); return; }
    if (!roleLoading && telegramId) navigate({ to: "/dashboard" });
  }, [loading, roleLoading, session, telegramId, navigate]);

  async function recheck() {
    setChecking(true);
    try { await refreshRole(); } finally { setChecking(false); }
  }

  if (loading || roleLoading) {
    return (
      <AuthLayout title="Loading…">
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Link your Telegram"
      subtitle="Connect your ClipsFlow account to your FriskyDev account."
      footer={<button onClick={() => signOut().then(() => navigate({ to: "/login" }))} className="text-muted-foreground hover:text-foreground">Sign out</button>}
    >
      <div className="mb-5 rounded-lg border border-border/60 bg-muted/30 p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-primary/10 p-2">
            <MessageCircle className="h-4 w-4 text-primary" />
          </div>
          <div className="text-sm">
            <p className="font-medium text-foreground">Link from the bot</p>
            <ol className="mt-1 list-decimal space-y-1 pl-4 text-muted-foreground">
              <li>Open the ClipsFlow bot in Telegram.</li>
              <li>Send <code className="rounded bg-background px-1.5 py-0.5 text-xs">/link</code>.</li>
              <li>Tap <b>Link my FriskyDev account</b> and sign in with Google.</li>
            </ol>
            <p className="mt-2 text-muted-foreground">
              No code to paste — your account links automatically. Then tap refresh below.
            </p>
          </div>
        </div>
      </div>

      <Button onClick={recheck} className="w-full" disabled={checking}>
        {checking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
        I&apos;ve linked — refresh
      </Button>
    </AuthLayout>
  );
}
